/**
 * OmniLeagues — Draft Board (AD).
 *
 * ONE AD tool, three tabs:
 *   • Draft Board    — drag registrants into team boxes (gender + age enforced),
 *                      auto-placed on first open; Not Selected = refund list.
 *   • Rosters        — read-only roster view + Publish/Unpublish + Welcome Letter.
 *   • Welcome Letter — templated letters with #wildcards#, per registration.
 *
 * Everything (sport, season, levels, variants, colors) is data-driven from the
 * chosen registration + org config. Nothing is hardcoded here.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardList, Users, Mail, Loader2, Plus, X, Trash2, UploadCloud,
  DollarSign, Send, RefreshCw, Copy, FileText,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import WelcomeLetterEditor from './WelcomeLetterEditor';

const looksHtml = (s) => /<(?:p|br|strong|b|em|u|a|h[1-3]|span|ul|ol|li)\b/i.test(s || '');
const escHtml = (t) => (t || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
// Legacy plain-text templates auto-convert to <p> paragraphs on load.
const plainToHtml = (t) => (t || '').split(/\n\s*\n/).map(
  (p) => `<p>${escHtml(p).replace(/\n/g, '<br>')}</p>`).join('') || '<p></p>';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/draft-board`;
const REG_API = `${process.env.REACT_APP_BACKEND_URL}/api/reg-submissions`;
const FORMS_API = `${process.env.REACT_APP_BACKEND_URL}/api/reg-builder`;

const WILDCARDS = ['Date', 'Mom', 'Dad', 'Parents', 'Player', 'PlayerFirst',
  'Team', 'Level', 'Season', 'Sport', 'Org', 'AD', 'ADTitle', 'ADEmail', 'ADPhone'];
const ROW_LABEL = { female: 'Girls', male: 'Boys', coed: 'Coed' };

// ── Pill ─────────────────────────────────────────────────────────────────────
// Compact rounded pill: light tint bg + colored border + dot, name on ONE line
// (ellipsis if long), age shown small after the name (+ tooltip). Drag-and-drop
// is the ONLY way to move (no dropdown). Pills flow inline and wrap in the box.
function Pill({ p, onDragStart, extra, readOnly, onResend }) {
  const border = p.color || '#e2e8f0';
  return (
    <div
      draggable={!readOnly}
      onDragStart={readOnly ? undefined : (e) => { e.dataTransfer.setData('text/plain', p.submission_id); if (onDragStart) onDragStart(p); }}
      title={[p.age != null ? `Age ${p.age} at cutoff` : p.name, p.group ? `Group: ${p.group}` : '', p.payment_status ? `Payment: ${p.payment_status}` : '',
        p.last_move ? `Last move: ${p.last_move.to === 'not_selected' || !p.last_move.to ? 'Not Selected' : p.last_move.to} · ${String(p.last_move.at || '').slice(0, 16).replace('T', ' ')}${p.last_move.by ? ` · ${p.last_move.by}` : ''}` : ''].filter(Boolean).join('\n')}
      className={`inline-flex items-center gap-1.5 rounded-full pl-2 pr-2 py-0.5 text-xs select-none max-w-[12rem] ${readOnly ? '' : 'cursor-grab'}`}
      style={{ background: `${border}40`, border: `1px solid ${border}` }}
      data-testid={`${readOnly ? 'db-roster' : 'db-pill'}-${p.submission_id}`}
    >
      <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ background: border }} />
      <span className="font-medium truncate" style={{ color: '#334155' }}>{p.name}</span>
      {p.age != null && <span className="text-[10px] text-slate-500 shrink-0">{p.age}{extra}</span>}
      {onResend && <button onClick={() => onResend(p.submission_id)} className="text-slate-400 hover:text-amber-600 shrink-0" title="Resend welcome letter" data-testid={`db-resend-${p.submission_id}`}><Send className="h-3 w-3" /></button>}
    </div>
  );
}

const _pillEligible = (box, p) => !p
  || ((box.gender === 'coed' || !p.gender || box.gender === p.gender)
      && (box.max_age == null || p.age == null || p.age <= box.max_age));

// ── Box ──────────────────────────────────────────────────────────────────────
function Box({ box, onMove, onColor, onRemove, readOnly, onResend, draggedPlayer, onDragStart }) {
  const [hover, setHover] = useState(null); // 'ok' | 'bad' | null
  const bad = hover === 'bad';
  return (
    <div
      className="rounded-lg border p-2 min-h-[6rem] self-start"
      style={{ borderColor: bad ? '#ef4444' : box.color, borderWidth: bad ? 2 : 1, background: `${box.color}26` }}
      onDragOver={(e) => {
        if (readOnly) return;
        const ok = _pillEligible(box, draggedPlayer);
        setHover(ok ? 'ok' : 'bad');
        e.dataTransfer.dropEffect = ok ? 'move' : 'none';
        if (ok) e.preventDefault(); // block the drop entirely when ineligible
      }}
      onDragLeave={() => setHover(null)}
      onDrop={(e) => { if (readOnly) return; setHover(null); const sid = e.dataTransfer.getData('text/plain'); if (sid) onMove(sid, box.id); }}
      data-testid={`db-box-${box.id}`}
    >
      <div className="flex items-center gap-1 mb-1">
        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: box.color }} />
        <span className="text-xs font-semibold text-slate-700 flex-1">
          {box.level_label}{box.variant ? ` ${box.variant.charAt(0).toUpperCase() + box.variant.slice(1)}` : ''}
        </span>
        <span className="text-[10px] text-slate-500">{box.players.length}</span>
        {!readOnly && (
          <>
            <input type="color" value={box.color} onChange={(e) => onColor(box.id, e.target.value)}
              className="h-4 w-4 border-0 bg-transparent p-0 cursor-pointer" title="Box color"
              data-testid={`db-color-${box.id}`} />
            <button onClick={() => onRemove(box.id)} className="text-slate-400 hover:text-red-500"
              title="Remove box" data-testid={`db-remove-box-${box.id}`}><X className="h-3.5 w-3.5" /></button>
          </>
        )}
      </div>
      {box.players.length === 0
        ? <p className="text-[10px] text-slate-400 pl-1">No players</p>
        : (
          <div className="flex flex-wrap gap-1">
            {box.players.map((p) => (
              <Pill key={p.submission_id} p={{ ...p, color: box.color }} readOnly={readOnly}
                onDragStart={onDragStart} onResend={readOnly ? onResend : undefined} />
            ))}
          </div>
        )}
    </div>
  );
}

// ── Refund modal ───────────────────────────────────────────────────────────
function RefundModal({ orgId, formId, onClose }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/${orgId}/refund-list?form_id=${formId}`).then((r) => setRows(r.data.rows || [])).catch(() => setRows([]));
  }, [orgId, formId]);
  useEffect(() => { load(); }, [load]);
  const markRefunded = async (sid) => {
    try { await axios.post(`${REG_API}/${orgId}/submissions/${sid}/record-refund`, {}); toast.success('Marked refunded'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not record refund'); }
  };
  const exportCsv = () => {
    const head = ['Player', 'Family', 'Email', 'Fee', 'Amount paid', 'Payment status', 'Refunded'];
    const lines = [head.join(',')].concat((rows || []).map((r) => [r.player, r.family, r.email || '', r.fee, r.amount_paid, r.payment_status || '', r.refunded ? 'yes' : 'no'].map((x) => `"${String(x ?? '').replace(/"/g, '""')}"`).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'refund-list.csv'; a.click();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose} data-testid="db-refund-modal">
      <div className="w-full max-w-2xl rounded-xl border border-slate-200 bg-white p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-slate-900 flex items-center gap-2"><DollarSign className="h-4 w-4 text-amber-500" /> Refund list</h4>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exportCsv} data-testid="db-refund-csv">Export CSV</Button>
            <button onClick={onClose} className="text-slate-400"><X className="h-4 w-4" /></button>
          </div>
        </div>
        {rows === null ? <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          : rows.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center">No not-selected registrants.</p>
            : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-slate-500 border-b"><th className="py-1">Player</th><th>Family</th><th>Amount paid</th><th>Payment</th><th></th></tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.submission_id} className="border-b border-slate-100 text-slate-700">
                        <td className="py-1.5 font-medium">{r.player}</td>
                        <td>{r.family}</td>
                        <td>{r.amount_paid > 0 ? `$${r.amount_paid.toFixed(2)}` : <span className="text-slate-400">not collected</span>}</td>
                        <td>{r.payment_status || '—'}</td>
                        <td className="text-right">
                          {r.refunded ? <span className="text-emerald-600">Refunded</span>
                            : <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => markRefunded(r.submission_id)} data-testid={`db-refund-${r.submission_id}`}>Mark refunded</Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function DraftBoardPanel({ orgId }) {
  const [tab, setTab] = useState('board');
  const [forms, setForms] = useState([]);
  const [formId, setFormId] = useState('');
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nsQ, setNsQ] = useState(''); const [nsGroup, setNsGroup] = useState(''); const [nsPay, setNsPay] = useState(''); const [nsSort, setNsSort] = useState('name');
  const [showRefund, setShowRefund] = useState(false);
  const [showAddBox, setShowAddBox] = useState(false);
  const [addLevel, setAddLevel] = useState('');
  const [addVariant, setAddVariant] = useState('');
  const [addVariantNew, setAddVariantNew] = useState('');

  const loadForms = useCallback(async () => {
    if (!orgId) return;
    try { const r = await axios.get(`${FORMS_API}/${orgId}/forms`); setForms(r.data.forms || []); if (!formId && r.data.forms?.length) setFormId(r.data.forms[0].id); }
    catch { setForms([]); }
  }, [orgId]); // eslint-disable-line

  const loadBoard = useCallback(async () => {
    if (!orgId || !formId) { setBoard(null); return; }
    setLoading(true);
    try { const r = await axios.get(`${API}/${orgId}/board?form_id=${formId}`); setBoard(r.data); }
    catch (e) { setBoard(null); toast.error(e.response?.data?.detail || 'Could not load board'); }
    finally { setLoading(false); }
  }, [orgId, formId]);

  useEffect(() => { loadForms(); }, [loadForms]);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  const allBoxes = board ? board.rows.flatMap((r) => r.boxes) : [];

  const move = async (sid, boxId) => {
    try {
      const body = { form_id: formId, submission_id: sid };
      if (boxId === 'not_selected') body.to = 'not_selected'; else body.box_id = boxId;
      const r = await axios.patch(`${API}/${orgId}/move`, body);
      setBoard(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Move blocked'); }
  };
  const setColor = async (boxId, color) => {
    try { const r = await axios.patch(`${API}/${orgId}/box`, { form_id: formId, box_id: boxId, color }); setBoard(r.data); } catch { toast.error('Could not set color'); }
  };
  const removeBox = async (boxId) => {
    if (!window.confirm('Remove this box? Its players move to the next-lowest box they qualify for, else Not Selected.')) return;
    try { const r = await axios.delete(`${API}/${orgId}/box`, { data: { form_id: formId, box_id: boxId } }); setBoard(r.data); } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove box'); }
  };
  const addBox = async () => {
    const variant = addVariant === '__new__' ? addVariantNew.trim() : addVariant;
    if (!addLevel) { toast.error('Pick a level'); return; }
    try {
      const r = await axios.post(`${API}/${orgId}/box`, { form_id: formId, level: addLevel, variant });
      setBoard(r.data); setShowAddBox(false); setAddLevel(''); setAddVariant(''); setAddVariantNew('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add box'); }
  };
  const togglePublish = async (publish) => {
    setBusy(true);
    try { const r = await axios.post(`${API}/${orgId}/publish`, { form_id: formId, publish }); toast.success(`${publish ? 'Published' : 'Unpublished'} ${r.data.changed} player(s)`); loadBoard(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); } finally { setBusy(false); }
  };

  // Columns are automatic: as many per row as the row with the most boxes.
  const cols = Math.max(1, ...((board?.rows || []).map((r) => r.boxes.length)), 1);
  const gridStyle = { display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '0.5rem' };

  const TABS = [
    { key: 'board', label: 'Draft Board', icon: ClipboardList },
    { key: 'rosters', label: 'Rosters', icon: Users },
  ];

  return (
    <div className="space-y-4" data-testid="draft-board-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {TABS.map((t) => {
            const Icon = t.icon; const active = tab === t.key;
            return (
              <Button key={t.key} variant="ghost" size="sm" onClick={() => setTab(t.key)}
                className={`h-8 px-3 text-xs font-medium border ${active ? 'bg-amber-50 text-amber-700 border-amber-200' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
                data-testid={`db-tab-${t.key}`}>
                <Icon className="h-3.5 w-3.5 mr-1.5" />{t.label}
              </Button>
            );
          })}
        </div>
        <select className="h-8 text-sm rounded px-2 border border-slate-300 bg-white text-slate-900"
          value={formId} onChange={(e) => setFormId(e.target.value)} data-testid="db-form-select">
          <option value="">Pick a registration…</option>
          {forms.map((f) => <option key={f.id} value={f.id}>{f.name}{f.season ? ` — ${f.season}` : ''}</option>)}
        </select>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        : !board ? <div className="border rounded-lg p-10 text-center bg-white border-slate-200"><p className="text-slate-500">Pick a registration to build its draft board.</p></div>
          : (
            <>
              {tab === 'board' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 flex-wrap text-sm text-slate-600 bg-white border border-slate-200 rounded-lg px-3 py-2">
                    <span className="font-semibold text-slate-800">{board.form.sport} · {board.form.season}</span>
                    <Button size="sm" className="ml-auto h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={() => setShowAddBox(true)} data-testid="db-add-box-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add box</Button>
                  </div>

                  {board.rows.map((row) => row.boxes.length > 0 && (
                    <div key={row.gender}>
                      <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{ROW_LABEL[row.gender] || row.gender}</p>
                      <div style={gridStyle}>
                        {row.boxes.map((b) => <Box key={b.id} box={b} allBoxes={allBoxes} onMove={move} onColor={setColor} onRemove={removeBox} />)}
                      </div>
                    </div>
                  ))}

                  <div>
                    {board.needs_max_ages && (
                      <p className="text-[11px] rounded border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1 mb-1" data-testid="db-needs-max-ages">
                        New registrants are placed in their lowest qualifying team automatically once each team has a <b>max age</b> (Teams ▸ Team Costs). Until then they wait here.
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mb-1" data-testid="db-ns-filters">
                      <input className="h-6 text-[11px] rounded border border-slate-300 px-1" placeholder="Search name" value={nsQ} onChange={(e) => setNsQ(e.target.value)} data-testid="db-ns-search" />
                      <select className="h-6 text-[11px] rounded border border-slate-300" value={nsGroup} onChange={(e) => setNsGroup(e.target.value)} data-testid="db-ns-group">
                        <option value="">All groups</option>{[...new Set(board.not_selected.players.map((p) => p.group).filter(Boolean))].map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                      <select className="h-6 text-[11px] rounded border border-slate-300" value={nsPay} onChange={(e) => setNsPay(e.target.value)} data-testid="db-ns-pay">
                        <option value="">Any payment</option>{[...new Set(board.not_selected.players.map((p) => p.payment_status).filter(Boolean))].map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                      <select className="h-6 text-[11px] rounded border border-slate-300" value={nsSort} onChange={(e) => setNsSort(e.target.value)} data-testid="db-ns-sort">
                        <option value="name">Sort: name</option><option value="date">Sort: registration date</option><option value="age">Sort: age</option>
                      </select>
                    </div>
                    <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1 flex items-center justify-between">
                      <span>Not Selected</span>
                      <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => setShowRefund(true)} data-testid="db-refund-list-btn">Refund List</Button>
                    </p>
                    <div className="rounded-lg border border-slate-300 bg-slate-50 p-2 min-h-[5rem]"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { const sid = e.dataTransfer.getData('text/plain'); if (sid) move(sid, 'not_selected'); }}
                      data-testid="db-not-selected">
                      <div style={gridStyle}>
                        {board.not_selected.players.length === 0
                          ? <p className="text-[10px] text-slate-400 pl-1">No players</p>
                          : board.not_selected.players
                            .filter((p) => (!nsQ || (p.name || '').toLowerCase().includes(nsQ.toLowerCase())) && (!nsGroup || p.group === nsGroup) && (!nsPay || p.payment_status === nsPay))
                            .sort((a, b) => nsSort === 'date' ? String(a.registered_at || '').localeCompare(String(b.registered_at || '')) : nsSort === 'age' ? (a.age ?? 0) - (b.age ?? 0) : (a.name || '').localeCompare(b.name || ''))
                            .map((p) => (
                            <Pill key={p.submission_id} p={{ ...p, color: '#94a3b8' }} boxes={allBoxes} onMove={move}
                              extra={p.amount_paid > 0 ? ` · $${p.amount_paid.toFixed(0)}` : ''} />
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tab === 'rosters' && <RostersTab board={board} gridStyle={gridStyle} busy={busy} onPublish={togglePublish} />}
            </>
          )}

      {showRefund && <RefundModal orgId={orgId} formId={formId} onClose={() => setShowRefund(false)} />}
      {showAddBox && board && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAddBox(false)} data-testid="db-add-box-modal">
          <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h4 className="font-bold text-slate-900">Add box</h4><button onClick={() => setShowAddBox(false)} className="text-slate-400"><X className="h-4 w-4" /></button></div>
            <label className="block text-xs text-slate-500">Level
              <select className="h-8 text-sm rounded border border-slate-300 w-full mt-1" value={addLevel} onChange={(e) => setAddLevel(e.target.value)} data-testid="db-add-level">
                <option value="">Pick a level…</option>
                {board.level_options.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </label>
            <label className="block text-xs text-slate-500">Variant
              <select className="h-8 text-sm rounded border border-slate-300 w-full mt-1" value={addVariant} onChange={(e) => setAddVariant(e.target.value)} data-testid="db-add-variant">
                <option value="">None</option>
                {board.variant_options.map((v) => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
                <option value="__new__">Add new…</option>
              </select>
            </label>
            {addVariant === '__new__' && <Input className="h-8 text-sm" placeholder="New variant (e.g. Gold)" value={addVariantNew} onChange={(e) => setAddVariantNew(e.target.value)} data-testid="db-add-variant-new" />}
            <div className="flex justify-end"><Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700" onClick={addBox} data-testid="db-add-box-confirm">Add</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rosters tab ────────────────────────────────────────────────────────────
function RostersTab({ board, gridStyle, busy, onPublish }) {
  const [pubConfirm, setPubConfirm] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap bg-white border border-slate-200 rounded-lg px-3 py-2">
        <Button size="sm" className={`h-8 text-xs text-white ${board.published ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 hover:bg-slate-500'}`}
          disabled={busy} onClick={() => setPubConfirm(true)} data-testid="db-publish-toggle">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><UploadCloud className="h-3.5 w-3.5 mr-1" />{board.published ? 'Published' : 'Unpublished'}</>}
        </Button>
      </div>

      {board.rows.map((row) => row.boxes.length > 0 && (
        <div key={row.gender}>
          <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{ROW_LABEL[row.gender] || row.gender}</p>
          <div style={gridStyle}>
            {row.boxes.map((b) => <Box key={b.id} box={b} readOnly onMove={() => {}} onColor={() => {}} onRemove={() => {}} />)}
          </div>
        </div>
      ))}

      {pubConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPubConfirm(false)} data-testid="db-publish-confirm">
          <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 space-y-3 text-center" onClick={(e) => e.stopPropagation()}>
            <UploadCloud className={`h-8 w-8 mx-auto ${board.published ? 'text-slate-500' : 'text-emerald-600'}`} />
            <p className="text-sm text-slate-700">{board.published
              ? 'Unpublish these rosters? This season\u2019s memberships and WP roster entries for board players will be removed.'
              : 'Publish these rosters? Every rostered player will be pushed live to the official datasets and WordPress.'}</p>
            <div className="flex justify-center gap-2"><Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setPubConfirm(false)}>Cancel</Button>
              <Button size="sm" className={`h-8 text-xs text-white ${board.published ? 'bg-slate-500 hover:bg-slate-600' : 'bg-emerald-600 hover:bg-emerald-700'}`} onClick={() => { setPubConfirm(false); onPublish(!board.published); }} data-testid="db-publish-confirm-btn">
                {board.published ? 'Unpublish' : 'Publish'}</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Letterhead (AD Office) settings panel ────────────────────────────────────
function LetterheadPanel({ orgId, onClose }) {
  const [lh, setLh] = useState(null);
  const [opts, setOpts] = useState([]);
  const [orgName, setOrgName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/${orgId}/letterhead`).then((r) => {
      setLh(r.data.letterhead); setOpts(r.data.sport_options || []); setOrgName(r.data.org_name || '');
    }).catch(() => toast.error('Could not load letterhead'));
  }, [orgId]);

  if (!lh) return <div className="p-4 text-xs text-slate-400" data-testid="db-letterhead-loading">Loading letterhead…</div>;

  const set = (k, v) => setLh((s) => ({ ...s, [k]: v }));
  const toggleSport = (name) => setLh((s) => {
    const cur = s.sports || [];
    return { ...s, sports: cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name] };
  });
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/${orgId}/letterhead`, {
        header_color: lh.header_color, office_line: lh.office_line, closing: lh.closing,
        sports: lh.sports, show_address: lh.show_address, show_website: lh.show_website });
      setLh(r.data.letterhead); toast.success('Letterhead saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };
  const hc = /^#[0-9a-fA-F]{3,8}$/.test(lh.header_color || '') ? lh.header_color : '#6B1F2E';
  const sportsPreview = (lh.sports || []).map((s) => s.toUpperCase()).join('  |  ');

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4" data-testid="db-letterhead-panel">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><FileText className="h-4 w-4" /> AD Office Letterhead</h4>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="db-letterhead-close"><X className="h-4 w-4" /></button>
      </div>

      {/* Live preview */}
      <div className="rounded-md overflow-hidden border border-slate-200" data-testid="db-letterhead-preview">
        <div className="px-4 py-3 bg-white">
          <div className="text-sm font-bold leading-tight" style={{ color: hc }}>{orgName || 'Your Organization'}</div>
          <div className="text-[10px] uppercase tracking-[0.15em]" style={{ color: hc }}>{lh.office_line || ' '}</div>
        </div>
        {(lh.sports || []).length > 0 && (
          <div className="px-4 py-1.5 text-right" style={{ borderBottom: `1px solid ${hc}` }}>
            <span className="text-[10px] font-bold tracking-wide" style={{ color: hc }}>{sportsPreview}</span>
          </div>
        )}
        <div className="px-4 pt-2 text-[11px] text-slate-400">#Date#</div>
        <div className="px-4 pb-2 text-[11px] text-slate-400">RE: #Season# #Sport# #Team#</div>
        <div className="px-4 py-2 text-center text-[10px] text-white" style={{ background: hc }}>
          {[lh.show_address !== false ? 'Address' : null, lh.show_website !== false ? 'Website' : null, 'Phone'].filter(Boolean).join('  |  ')}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs text-slate-600 space-y-1 block">
          <span>Header colour</span>
          <span className="flex items-center gap-2">
            <input type="color" value={hc} onChange={(e) => set('header_color', e.target.value)} className="h-8 w-10 rounded border border-slate-200 p-0.5" data-testid="db-letterhead-color" />
            <Input className="h-8 text-xs" value={lh.header_color || ''} onChange={(e) => set('header_color', e.target.value)} data-testid="db-letterhead-color-hex" />
          </span>
        </label>
        <label className="text-xs text-slate-600 space-y-1 block">
          <span>Office line</span>
          <Input className="h-8 text-xs" value={lh.office_line || ''} onChange={(e) => set('office_line', e.target.value)} data-testid="db-letterhead-office" />
        </label>
        <label className="text-xs text-slate-600 space-y-1 block">
          <span>Closing</span>
          <Input className="h-8 text-xs" value={lh.closing || ''} onChange={(e) => set('closing', e.target.value)} data-testid="db-letterhead-closing" />
        </label>
        <div className="text-xs text-slate-600 space-y-1">
          <span>Sports shown</span>
          <div className="flex flex-wrap gap-2 pt-1">
            {opts.map((s) => (
              <label key={s} className="flex items-center gap-1 cursor-pointer" data-testid={`db-letterhead-sport-${s}`}>
                <input type="checkbox" checked={(lh.sports || []).includes(s)} onChange={() => toggleSport(s)} />
                <span>{s}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid="db-letterhead-show-address">
          <input type="checkbox" checked={!!lh.show_address} onChange={(e) => set('show_address', e.target.checked)} /> Show address in footer
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid="db-letterhead-show-website">
          <input type="checkbox" checked={!!lh.show_website} onChange={(e) => set('show_website', e.target.checked)} /> Show website in footer
        </label>
        <Button size="sm" className="ml-auto h-8 text-xs bg-amber-600 hover:bg-amber-700" onClick={save} disabled={saving} data-testid="db-letterhead-save">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save letterhead'}</Button>
      </div>
    </div>
  );
}

// ── Welcome Letter tab ───────────────────────────────────────────────────────
export function WelcomeTab({ orgId, formId, published }) {
  const [letters, setLetters] = useState([]);
  const [sel, setSel] = useState('');
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [useLetterhead, setUseLetterhead] = useState(true);
  const [showLh, setShowLh] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  const focusRef = useRef('body');

  const load = useCallback(() => {
    axios.get(`${API}/${orgId}/welcome-letters?form_id=${formId}`).then((r) => setLetters(r.data.letters || [])).catch(() => setLetters([]));
  }, [orgId, formId]);
  useEffect(() => { load(); }, [load]);

  const openLetter = (l) => { setSel(l.id); setName(l.name || ''); setSubject(l.subject || ''); setBody(looksHtml(l.body) ? l.body : plainToHtml(l.body)); setUseLetterhead(l.use_letterhead !== false); setPreview(null); };
  const newLetter = () => { setSel(''); setName('New letter'); setSubject(''); setBody('<p></p>'); setUseLetterhead(true); setPreview(null); };

  const insert = (token) => {
    const tag = `#${token}#`;
    if (focusRef.current === 'subject') { setSubject((s) => s + tag); return; }
    if (editorRef.current) { editorRef.current.insertWildcard(token); return; }
    setBody((b) => b + tag);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (sel) { await axios.put(`${API}/${orgId}/welcome-letters/${sel}`, { form_id: formId, name, subject, body, use_letterhead: useLetterhead }); }
      else { const r = await axios.post(`${API}/${orgId}/welcome-letters`, { form_id: formId, name, subject, body, use_letterhead: useLetterhead }); setSel(r.data.letter.id); }
      toast.success('Saved'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };
  const doPreview = async () => {
    if (!sel) { toast.error('Save the letter first'); return; }
    try { const r = await axios.get(`${API}/${orgId}/welcome-letters/${sel}/preview?form_id=${formId}`); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
  };
  const duplicate = async () => {
    if (!sel) { toast.error('Select a template first'); return; }
    try {
      const r = await axios.post(`${API}/${orgId}/welcome-letters/${sel}/duplicate`, {});
      toast.success('Duplicated'); await load(); openLetter(r.data.letter);
    } catch (e) { toast.error(e.response?.data?.detail || 'Duplicate failed'); }
  };

  // Recipients: ONE row per rostered player (all parents shown together) + send.
  const [recips, setRecips] = useState([]);
  const [checked, setChecked] = useState({});
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [asHtml, setAsHtml] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const loadRecips = useCallback(() => {
    axios.get(`${API}/${orgId}/welcome-recipients?form_id=${formId}`).then((r) => setRecips(r.data.rows || [])).catch(() => setRecips([]));
  }, [orgId, formId]);
  useEffect(() => { loadRecips(); }, [loadRecips, published]);
  const checkedRows = recips.filter((r) => checked[r.submission_id]);
  const nPlayers = checkedRows.length;
  const nEmails = checkedRows.reduce((a, r) => a + (r.email_count || 0), 0);
  const allChecked = recips.length > 0 && checkedRows.length === recips.length;
  const toggleAll = () => { const next = {}; if (!allChecked) recips.forEach((r) => { next[r.submission_id] = true; }); setChecked(next); };
  const send = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/${orgId}/welcome-letters/${sel}/send`, {
        form_id: formId, as_html: asHtml, submission_ids: checkedRows.map((x) => x.submission_id) });
      toast.success(`Sent ${r.data.emails} email(s) for ${r.data.players} player(s)`);
      setConfirm(false); setChecked({}); loadRecips();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); } finally { setSending(false); }
  };
  const sendTest = async () => {
    if (!sel) { toast.error('Save the letter first'); return; }
    if (!testEmail.trim()) { toast.error('Enter a test email address'); return; }
    setTesting(true);
    try {
      const r = await axios.post(`${API}/${orgId}/welcome-letters/${sel}/send-test`, {
        form_id: formId, email: testEmail.trim(), as_html: asHtml,
        submission_id: checkedRows[0]?.submission_id });
      toast.success(`Test sent to ${r.data.sent_to} (rendered for ${r.data.rendered_for})`);
      setTestEmail(''); setChecked({});
    } catch (e) { toast.error(e.response?.data?.detail || 'Test send failed'); } finally { setTesting(false); }
  };
  // Typing a test address auto-selects ONLY the first player; clearing it frees
  // the list again (Select all works normally).
  const onTestEmailChange = (v) => {
    setTestEmail(v);
    if (v.trim() && recips.length) setChecked({ [recips[0].submission_id]: true });
    else if (!v.trim()) setChecked({});
  };

  return (
    <div className="space-y-4">
    {showLh && <LetterheadPanel orgId={orgId} onClose={() => setShowLh(false)} />}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 bg-white border border-slate-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between"><h4 className="text-sm font-semibold text-slate-800">Templates <span className="text-[10px] font-normal text-slate-400">(org library)</span></h4>
          <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={newLetter} data-testid="db-letter-new"><Plus className="h-3.5 w-3.5 mr-1" /> Add new</Button></div>
        <Button size="sm" variant="outline" className="h-7 text-xs w-full" onClick={() => setShowLh((v) => !v)} data-testid="db-letterhead-toggle"><FileText className="h-3.5 w-3.5 mr-1" /> {showLh ? 'Hide letterhead' : 'Letterhead'}</Button>
        {letters.length === 0 ? <p className="text-xs text-slate-400">No templates yet.</p>
          : letters.map((l) => (
            <button key={l.id} onClick={() => openLetter(l)} className={`flex items-center justify-between w-full text-left text-sm px-2 py-1.5 rounded ${sel === l.id ? 'bg-amber-50 text-amber-700' : 'text-slate-700 hover:bg-slate-100'}`} data-testid={`db-letter-${l.id}`}>
              <span className="truncate">{l.name}</span>
              {l.sport && <span className="ml-2 shrink-0 text-[9px] uppercase tracking-wide px-1 py-0.5 rounded bg-slate-100 text-slate-500">{l.sport}</span>}
            </button>
          ))}
      </div>

      <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid="db-letter-editor">
        <Input className="h-8 text-sm" placeholder="Template name" value={name} onChange={(e) => setName(e.target.value)} data-testid="db-letter-name" />
        <Input className="h-8 text-sm" placeholder="Subject" value={subject} onFocus={() => { focusRef.current = 'subject'; }} onChange={(e) => setSubject(e.target.value)} data-testid="db-letter-subject" />
        <div className="flex flex-wrap gap-1">
          {WILDCARDS.map((w) => <button key={w} onClick={() => insert(w)} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-slate-200" data-testid={`wildcard-${w}`}>#{w}#</button>)}
        </div>
        <div onFocus={() => { focusRef.current = 'body'; }} data-testid="db-letter-body">
          <WelcomeLetterEditor ref={editorRef} value={body} onChange={setBody} />
        </div>
        <div className="flex gap-2 items-center">
          <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700" onClick={save} disabled={saving} data-testid="db-letter-save">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={duplicate} disabled={!sel} data-testid="db-letter-duplicate"><Copy className="h-3.5 w-3.5 mr-1" /> Duplicate</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={doPreview} data-testid="db-letter-preview"><RefreshCw className="h-3.5 w-3.5 mr-1" /> Preview</Button>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 ml-auto cursor-pointer" data-testid="db-letter-uselh-label">
            <input type="checkbox" checked={useLetterhead} onChange={(e) => setUseLetterhead(e.target.checked)} data-testid="db-letter-uselh" /> Use letterhead
          </label>
        </div>
        {preview && (
          <div className="border border-slate-200 rounded p-3 bg-slate-50 text-sm" data-testid="db-letter-preview-out">
            <p className="text-xs text-slate-400">Preview for {preview.sample_player}</p>
            <p className="font-semibold text-slate-800 mt-1">{preview.subject}</p>
            {preview.is_html
              ? <div className="prose prose-sm max-w-none mt-1 text-slate-700" data-testid="db-letter-preview-html" dangerouslySetInnerHTML={{ __html: preview.body }} />
              : <p className="text-slate-700 whitespace-pre-wrap mt-1">{preview.body}</p>}
          </div>
        )}
      </div>
    </div>

    {/* Recipients + Send */}
    <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid="db-recipients">
      <div className="flex items-center gap-3 flex-wrap">
        <h4 className="text-sm font-semibold text-slate-800">Recipients</h4>
        <button className="text-[11px] text-slate-500 hover:text-slate-800 underline" onClick={toggleAll} data-testid="db-recip-selectall">{allChecked ? 'Unselect all' : 'Select all'}</button>
        <span className="text-[11px] text-slate-400">{nPlayers} player(s) · {nEmails} email(s) selected · {recips.length} rostered</span>
        <label className="flex items-center gap-1.5 text-[11px] text-slate-600 ml-2 cursor-pointer" data-testid="db-send-html-label">
          <input type="checkbox" checked={asHtml} onChange={(e) => setAsHtml(e.target.checked)} data-testid="db-send-html-check" /> Send as HTML
        </label>
        <Button size="sm" className="ml-auto h-9 px-4 text-sm bg-blue-600 hover:bg-blue-700 text-white" disabled={!published || !sel || checkedRows.length === 0} onClick={() => setConfirm(true)} data-testid="db-send-welcome-btn"><Send className="h-4 w-4 mr-1.5" /> Send Welcome Letter</Button>
      </div>
      {!published && <p className="text-[11px] text-amber-600">Publish the rosters (Rosters tab) before sending.</p>}
      {published && !sel && <p className="text-[11px] text-slate-400">Select a template above to send.</p>}

      {/* Send test */}
      <div className="flex items-center gap-2 flex-wrap border border-dashed border-slate-200 rounded p-2 bg-slate-50" data-testid="db-sendtest">
        <span className="text-[11px] font-medium text-slate-600">Send test:</span>
        <Input className="h-8 text-xs w-56" placeholder="test@email.com" value={testEmail} onChange={(e) => onTestEmailChange(e.target.value)} data-testid="db-sendtest-email" />
        <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!sel || testing} onClick={sendTest} data-testid="db-sendtest-btn">{testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Send test</>}</Button>
        {testEmail.trim() && recips.length > 0
          ? <span className="text-[10px] text-slate-500" data-testid="db-sendtest-note">Sends {recips[0].player}'s letter to {testEmail.trim()} only · not marked sent</span>
          : <span className="text-[10px] text-slate-400">Type an address to send the first player's letter to you only · works unpublished</span>}
      </div>

      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-500 border-b"><th className="py-1 w-6"></th><th>Player</th><th>Team</th><th>Parents</th><th>Emails</th><th>Status</th></tr></thead>
          <tbody>
            {recips.length === 0
              ? <tr><td colSpan={6} className="py-3 text-center text-slate-400">No rostered players yet.</td></tr>
              : recips.map((r) => (
                <tr key={r.submission_id} className="border-b border-slate-100 text-slate-700 align-top" data-testid={`db-recip-${r.submission_id}`}>
                  <td className="py-1.5"><input type="checkbox" checked={!!checked[r.submission_id]} onChange={(e) => setChecked((c) => ({ ...c, [r.submission_id]: e.target.checked }))} data-testid={`db-recip-check-${r.submission_id}`} /></td>
                  <td className="font-medium">{r.player}</td>
                  <td>{r.team}</td>
                  <td>{r.parent_names || <span className="text-slate-300">—</span>}</td>
                  <td className="text-slate-500">{(r.emails || []).join(', ') || <span className="text-slate-300">no email</span>}</td>
                  <td>{r.sent ? <span className="text-emerald-600" title={r.sent_at || ''}>Sent</span> : <span className="text-slate-300">—</span>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>

    {confirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setConfirm(false)} data-testid="db-welcome-confirm">
        <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 space-y-3 text-center" onClick={(e) => e.stopPropagation()}>
          <Mail className="h-8 w-8 text-blue-500 mx-auto" />
          <p className="text-sm text-slate-700">Are you sure? Send to <strong>{nPlayers}</strong> player(s) across <strong>{nEmails}</strong> parent email(s)?</p>
          <div className="flex justify-center gap-2"><Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setConfirm(false)}>Cancel</Button>
            <Button size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={send} disabled={sending} data-testid="db-welcome-confirm-send">{sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Send</>}</Button></div>
        </div>
      </div>
    )}
    </div>
  );
}
